import React from 'react';
import { Box, Button, IconButton, OutlinedInput, Typography } from '@mui/material';
import { Add, DragIndicator, Remove } from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

export type Document = { label: string, url: string };

type Props = {
  id: number;
  content: Document[];
  onChange: (id: number, content: Document[]) => void;
}

export const DocumentList = ({ id, content, onChange }: Props) => {
  const handleChangeList = (e, index) => {
    onChange(id, content.map((p, i) => index === i ? { ...p, [e.target.name]: e.target.value } : p));
  };

  const handleAddDocument = () => {
    onChange(id, [...content, { label: '', url: '' }]);
  };

  const handleRemoveLine = (index: number) => {
    onChange(id, content.slice(0, index).concat(content.slice(index + 1)));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const updated = [...content];
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    onChange(id, updated);
  };

  return (
    <Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`document-list-${id}`}>
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              {content?.map((document, index) => (
                <Draggable key={`document-${id}-${index}`} draggableId={`document-${id}-${index}`} index={index}>
                  {(dragProvided) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        mb: 8,
                      }}
                    >
                      <Box
                        {...dragProvided.dragHandleProps}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'grab',
                          color: 'text.secondary',
                        }}
                      >
                        <DragIndicator />
                      </Box>

                      <Typography variant="caption" sx={{ minWidth: 36 }}>
                        Label:
                      </Typography>
                      <OutlinedInput
                        size="small"
                        name="label"
                        type="string"
                        value={document.label || ''}
                        onChange={(e) => handleChangeList(e, index)}
                        sx={{ flex: 1, minWidth: 0 }}
                      />

                      <Typography variant="caption" sx={{ minWidth: 24 }}>
                        Url:
                      </Typography>
                      <OutlinedInput
                        size="small"
                        name="url"
                        type="string"
                        value={document.url || ''}
                        onChange={(e) => handleChangeList(e, index)}
                        sx={{ flex: 1.5, minWidth: 0 }}
                      />

                      <IconButton size="small" onClick={() => handleRemoveLine(index)}>
                        <Remove />
                      </IconButton>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Button startIcon={<Add />} onClick={handleAddDocument}>
        Add
      </Button>
    </Box>
  )
}
